// Draft offer: a person asks the offer agent to work on one customer message
// (spec 14 §3.8.2, D-515 ruling 4). Two doors use it. The messaging thread
// offers it on the newest customer message, and an expired journal card of
// the offer agent offers it on the message its run came from (spec 14 §9
// item 11 as 14-A builds it). Both call `POST /v1/agents/:id/trigger`, which
// the server answers under the caller's own reach (D-519 ruling 5).
//
// The control is shown to a reader whose `GET /v1/agents` answers the offer
// agent (an `agents.invoke` holder of the sales domain, or a manager of it),
// and only while that agent is enabled: a paused agent refuses every trigger
// with `AGENT_PAUSED`, and it starts paused (D-507 ruling 5), so a button that
// could only be refused is not drawn.

import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { fetchRun, triggerOnMessage, useAgents } from "../hooks/agents-queries";
import { useAgentsPermissions } from "../hooks/permissions";
import type { AgentAction, AgentListItem } from "../lib/agents-lib";
import { errorText } from "../lib/labels";

/** The builtin code of the agent that drafts offers (spec 14 §3.8). */
export const OFFER_AGENT_CODE = "offer_agent";

export interface OfferAgentState {
  /** The offer agent as `GET /v1/agents` serves it to this reader, if at all. */
  readonly agent: AgentListItem | undefined;
  /** Whether Draft offer may be drawn: the reader is answered the agent and it is enabled. */
  readonly canDraft: boolean;
}

/** The offer agent this reader may ask for a draft, read once per screen. */
export function useOfferAgent(): OfferAgentState {
  const perms = useAgentsPermissions();
  const mayInvoke = perms.has("agents.invoke") || perms.has("agents.manage");
  const agents = useAgents(perms.isResolved && perms.canList && mayInvoke);
  const agent = mayInvoke
    ? agents.data?.find((row) => row.agent_code === OFFER_AGENT_CODE)
    : undefined;
  return { agent, canDraft: agent?.status === "enabled" };
}

/** Thrown when an expired card's run names no message to draft from. */
export class NoSourceMessage extends Error {
  constructor() {
    super("the run names no message");
    this.name = "NoSourceMessage";
  }
}

/** The message a run started from: a manual or intake run's `trigger_ref.message_id`. */
export function runMessageId(run: {
  trigger_ref: { message_id?: string | undefined };
}): string | undefined {
  return run.trigger_ref.message_id;
}

/**
 * Whether an action's card offers Draft offer: an expired proposal of the
 * offer agent that came from a run. A card the sweep expired cannot be
 * approved any more, and the only way forward is a fresh run with fresh
 * prices and stock (spec 14 §9 item 11).
 */
export function offersRedraft(action: AgentAction): boolean {
  return (
    action.status === "expired" && action.agent_code === OFFER_AGENT_CODE && action.run !== null
  );
}

/** The expired card's Draft offer, on the message its run came from. */
export function RedraftOffer({ action }: { action: AgentAction }) {
  if (!offersRedraft(action) || action.run === null) return null;
  return <RedraftControl runId={action.run.id} />;
}

type RedraftState =
  | { readonly kind: "idle" }
  | { readonly kind: "busy" }
  | { readonly kind: "started" }
  | { readonly kind: "failed"; readonly error: unknown };

function RedraftControl({ runId }: { runId: string }) {
  const { t } = useTranslation();
  const offer = useOfferAgent();
  const queryClient = useQueryClient();
  const [state, setState] = useState<RedraftState>({ kind: "idle" });
  if (!offer.canDraft || offer.agent === undefined) return null;
  const agentId = offer.agent.id;
  const start = async () => {
    setState({ kind: "busy" });
    try {
      const run = await fetchRun(runId);
      const messageId = runMessageId(run);
      if (messageId === undefined) throw new NoSourceMessage();
      await triggerOnMessage(agentId, messageId);
      setState({ kind: "started" });
      void queryClient.invalidateQueries({ queryKey: ["agents", "runs"] });
    } catch (error) {
      setState({ kind: "failed", error });
    }
  };
  return (
    <div className="ag-row" data-testid="action-redraft">
      {state.kind === "started" ? (
        <p className="ag-hint" role="status" data-testid="action-redraft-started">
          {t("agents.journal.redraft.started")}
        </p>
      ) : (
        <Button
          size="sm"
          data-testid="action-redraft-button"
          busy={state.kind === "busy"}
          busyLabel={t("agents.journal.redraft.busy")}
          onClick={() => {
            void start();
          }}
        >
          {t("agents.journal.redraft.button")}
        </Button>
      )}
      {state.kind === "failed" ? (
        <p className="ag-error" role="alert" data-testid="action-redraft-error">
          {state.error instanceof NoSourceMessage
            ? t("agents.journal.redraft.noMessage")
            : errorText(t, state.error)}
        </p>
      ) : null}
    </div>
  );
}
